/**
 * Customer-facing Notices feed.
 *
 * Combines advance notices, integration health notices and policy notices.
 * Also shows current + deprecated API versions.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, Info, ShieldAlert, Plug, Code2 } from "lucide-react";

export default function Notices() {
  const [notices, setNotices] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/governance/notices").then((r) => r.data.items || []),
      api.get("/governance/api-versions").then((r) => r.data.items || []).catch(() => []),
    ]).then(([n, v]) => { setNotices(n); setVersions(v); })
      .finally(() => setLoading(false));
  }, []);

  const advance = notices.filter((n) => n.category === "advance");
  const integration = notices.filter((n) => n.category === "integration");
  const policy = notices.filter((n) => n.category === "policy");

  return (
    <div className="max-w-3xl space-y-8" data-testid="notices-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Advance notices</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Coming changes & integrations</h1>
        <p className="text-sm text-slate-600 mt-3 max-w-xl">
          Anything that changes your APIs, authentication, workflows or integrations is announced here first —
          typically 30 to 90 days in advance so you can plan.
        </p>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      <Section icon={AlertTriangle} title="Advance notices" items={advance} empty="Nothing scheduled — we'll notify you here before anything customer-impacting ships." />
      <Section icon={Plug}          title="Integration health" items={integration} empty="No integration action required right now." />
      <Section icon={Info}          title="Policy updates" items={policy} empty="No policy updates pending." />

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Code2 className="h-4 w-4 text-slate-500" />
          <h2 className="text-lg font-heading font-semibold text-slate-900">API versions</h2>
        </div>
        {versions.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-4 text-sm text-slate-500">No versions recorded yet.</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md divide-y">
            {versions.map((v) => (
              <div key={v.id} className="p-4 flex items-start gap-4" data-testid={`api-version-${v.version}`}>
                <div className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${apiStateStyle(v.state)}`}>{v.state}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{v.version}</div>
                  {v.summary && <div className="text-xs text-slate-600 mt-1">{v.summary}</div>}
                  <div className="text-[11px] text-slate-500 mt-1">
                    Released {v.released_at}
                    {v.deprecation_date && ` · Deprecates ${v.deprecation_date}`}
                    {v.sunset_date && ` · Sunsets ${v.sunset_date}`}
                  </div>
                  {v.migration_url && <a href={v.migration_url} className="text-xs text-slate-900 underline underline-offset-2">Migration guide →</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Section({ icon: Icon, title, items, empty }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <h2 className="text-lg font-heading font-semibold text-slate-900">{title}</h2>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-4 text-sm text-slate-500">{empty}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md divide-y">
          {items.map((n) => (
            <div key={n.id} className="p-4 space-y-1" data-testid={`notice-item-${n.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${severityStyle(n.severity)}`}>{n.severity}</span>
                {n.action_required && (
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-rose-800 bg-rose-50 border border-rose-200 rounded px-1.5 flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" /> Action required
                  </span>
                )}
                <span className="text-sm font-semibold text-slate-900">{n.title}</span>
              </div>
              <div className="text-sm text-slate-700">{n.message}</div>
              <div className="text-xs text-slate-500">
                {n.effective_date && <span>Effective {n.effective_date}. </span>}
                {n.deadline && <span className="text-rose-700 font-medium">Deadline {n.deadline}. </span>}
                {(n.affected_services?.length > 0) && <span>Affects: {n.affected_services.join(", ")}. </span>}
              </div>
              {n.learn_more_url && <a href={n.learn_more_url} className="text-xs text-slate-900 underline underline-offset-2">Review integration →</a>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function severityStyle(s) {
  return {
    info:     "text-sky-700 bg-sky-50 border-sky-200",
    warning:  "text-amber-700 bg-amber-50 border-amber-200",
    critical: "text-rose-700 bg-rose-50 border-rose-200",
  }[s] || "text-slate-500 bg-slate-50 border-slate-200";
}

function apiStateStyle(s) {
  return {
    current:    "text-emerald-700 bg-emerald-50 border-emerald-200",
    deprecated: "text-amber-700 bg-amber-50 border-amber-200",
    sunset:     "text-rose-700 bg-rose-50 border-rose-200",
  }[s] || "text-slate-500 bg-slate-50 border-slate-200";
}
